import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PatientFilter, PatientWrite } from '@mc/shared/patients';

import { api, unwrap } from '../api/client';

import type { components } from '../api/generated/schema';

export type Patient = components['schemas']['Patient'];
export type PatientSummary = components['schemas']['PatientSummary'];

export interface PatientListParams {
  q?: string;
  branchId?: string;
  filter?: PatientFilter;
  limit?: number;
}

/** Claves de TanStack Query: `patient.updated` invalida ['patient', id] y ['patients'] (realtime/invalidations). */
export const patientKeys = {
  list: (params: PatientListParams) => ['patients', params] as const,
  one: (id: string) => ['patient', id] as const,
};

/** El cuerpo de la API: sin los campos vacíos del formulario (la API los trata como "sin dato"). */
export function toBody(values: PatientWrite): components['schemas']['PatientWrite'] {
  return Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== ''),
  ) as components['schemas']['PatientWrite'];
}

const clean = (params: PatientListParams) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== 'all'));

/** RF-PAC-01 / RF-GEN-04. Primera página (la lista agrega "Cargar más" con el cursor). */
export function usePatients(params: PatientListParams, enabled = true) {
  return useQuery({
    queryKey: patientKeys.list(params),
    queryFn: () => unwrap(api.GET('/patients', { params: { query: clean(params) } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function fetchMorePatients(params: PatientListParams, cursor: string) {
  return unwrap(api.GET('/patients', { params: { query: { ...clean(params), cursor } } }));
}

export function usePatient(id: string, enabled = true) {
  return useQuery({
    queryKey: patientKeys.one(id),
    queryFn: () => unwrap(api.GET('/patients/{id}', { params: { path: { id } } })),
    enabled,
  });
}

export function useCreatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: PatientWrite) => unwrap(api.POST('/patients', { body: toBody(values) })),
    onSuccess: (patient) => {
      queryClient.setQueryData(patientKeys.one(patient.id), patient);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

export function useUpdatePatient(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: PatientWrite) =>
      unwrap(api.PATCH('/patients/{id}', { params: { path: { id } }, body: toBody(values) })),
    onSuccess: (patient) => {
      queryClient.setQueryData(patientKeys.one(id), patient);
      void queryClient.invalidateQueries({ queryKey: ['patient', id] });
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}
